import React, { useEffect, useState } from 'react'
import {
  Button,
  TextField,
  Typography,
  Box,
  Modal,
  CircularProgress,
  styled,
  Grid,
  MenuItem,
  Autocomplete,
} from '@mui/material'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { toast } from 'react-toastify'

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import { SaleIntegrity, StateList, CountryList } from '../../types/SaleIntegrity'
import {
  useGetStateListQuery,
  useGetCountryListQuery,
  useUpdateDealerSaleMutation,
  useGetBrandsListQuery,
  useGetSubcategoriesQuery,
  useGetDealerGroupsQuery,
} from '../../redux/api/SaleIntegrityApi'
import { useAppSelector } from '../../redux/store'

const styles = {
  container: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '95%',
    maxWidth: 1000,
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '16px',
    overflow: 'hidden',
  },
  content: {
    padding: '8px 24px',
    maxHeight: '75vh',
    overflowY: 'auto',
  },
  headerStyle: {
    display: 'flex',
    p: 1.5,
    justifyContent: 'center',
    backgroundColor: '#1976d2',
  },
  footerStyle: {
    textAlign: 'right',
    padding: '12px 24px',
    borderTop: '1px solid #e0e0e0',
    backgroundColor: '#f9f9f9',
  },
}

const Item = styled(Box)({
  padding: '2px 8px',
})

interface Props {
  saleIntegrityData: SaleIntegrity | null
  onClose: () => void
  open: boolean
}

const validationSchema = yup.object({
  subcategory: yup.string().required('SubCategory is required'),
})

export const SaleIntegrityForm = ({ onClose, open, saleIntegrityData }: Props) => {
  const user = useAppSelector((state) => state.userState.user)
  const { data: countries = [], isLoading: countriesLoading } = useGetCountryListQuery(undefined, {
    skip: !open,
  })

  const { data: brandsList = [], isLoading: brandsLoading } = useGetBrandsListQuery(undefined, {
    skip: !open,
  })
  const { data: subcategories = [], isLoading: subcategoriesLoading } = useGetSubcategoriesQuery(
    undefined,
    {
      skip: !open,
    },
  )
  const { data: dealerData = [], isLoading: dealerLoading } = useGetDealerGroupsQuery(undefined, {
    skip: !open,
  })
  const [
    updateDealerSale,
    {
      data: uData,
      isLoading: uIsLoading,
      isError: uIsError,
      error: uError,
      isSuccess: uIsSuccess,
      reset: uReset,
    },
  ] = useUpdateDealerSaleMutation()
  const currentCountryString = String(saleIntegrityData?.country || '')
    .trim()
    .toLowerCase()

  const matchedCountry = countries.find((c: CountryList) => {
    const region = String(c.regionCode || '').toLowerCase()
    const code = String(c.countryCode || '').toLowerCase()
    const name = String(c.countryName || '').toLowerCase()

    return (
      region === currentCountryString ||
      code === currentCountryString ||
      name === currentCountryString
    )
  })

  const countryId = matchedCountry?.countryId
  const { data: statesList = [], isLoading: statesLoading } = useGetStateListQuery(countryId ?? 0, {
    skip: !countryId || !open,
  })

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return ''
    return dateString.split('T')[0]
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      address1: saleIntegrityData?.address1 ?? '',
      address2: saleIntegrityData?.address2 ?? '',
      application: saleIntegrityData?.application ?? '',
      brand: saleIntegrityData?.brand ?? '',
      city: saleIntegrityData?.city ?? '',
      contractCreateError: saleIntegrityData?.contractCreateError ?? '',
      contractCreateErrorDetail: saleIntegrityData?.contractCreateErrorDetail ?? '',
      contractNumber: saleIntegrityData?.contractNumber ?? '',
      contractSourceName: saleIntegrityData?.contractSourceName ?? '',
      country: saleIntegrityData?.country ?? '',
      createdBy: saleIntegrityData?.createdBy ?? '',
      createdOn: formatDate(saleIntegrityData?.createdOn),
      dealerGroupNumber: saleIntegrityData?.dealerGroupNumber ?? '',
      dealerId: saleIntegrityData?.dealerId ?? 0,
      dealerSalesId: saleIntegrityData?.dealerSalesId ?? 0,
      deliveryDt: formatDate(saleIntegrityData?.deliveryDt),
      email: saleIntegrityData?.email ?? '',
      firstName: saleIntegrityData?.firstName ?? '',
      homeClosingDt: formatDate(saleIntegrityData?.homeClosingDt),
      installDt: formatDate(saleIntegrityData?.installDt),
      invoiceDt: formatDate(saleIntegrityData?.invoiceDt),
      invoiceLineNumber: saleIntegrityData?.invoiceLineNumber ?? '',
      invoiceTicketNumber: saleIntegrityData?.invoiceTicketNumber ?? '',
      itemDescription: saleIntegrityData?.itemDescription ?? '',
      lastName: saleIntegrityData?.lastName ?? '',
      loanNumber: saleIntegrityData?.loanNumber ?? '',
      materialType: saleIntegrityData?.materialType ?? '',
      model: saleIntegrityData?.model ?? '',
      oemWarrantyCertificate: saleIntegrityData?.oemWarrantyCertificate ?? '',
      phone: saleIntegrityData?.phone ?? '',
      planPurchaseDt: formatDate(saleIntegrityData?.planPurchaseDt),
      postalCode: saleIntegrityData?.postalCode ?? '',
      protectionPlanType: saleIntegrityData?.protectionPlanType ?? '',
      quantity: saleIntegrityData?.quantity ?? 0,
      realtorEmail: saleIntegrityData?.realtorEmail ?? '',
      realtorFirstName: saleIntegrityData?.realtorFirstName ?? '',
      realtorLastName: saleIntegrityData?.realtorLastName ?? '',
      realtorProgram: saleIntegrityData?.realtorProgram ?? '',
      retailPrice: saleIntegrityData?.retailPrice ?? 0,
      retailProtectionPrice: saleIntegrityData?.retailProtectionPrice ?? 0,
      salesPersonId: saleIntegrityData?.salesPersonId ?? '',
      serial: saleIntegrityData?.serial ?? '',
      sku: saleIntegrityData?.sku ?? '',
      squareFootage: saleIntegrityData?.squareFootage ?? '',
      state: saleIntegrityData?.state ?? '',
      tax: saleIntegrityData?.tax ?? 0,
      totalInvoiceAmount: saleIntegrityData?.totalInvoiceAmount ?? 0,
      totalRetailOfCoveredItems: saleIntegrityData?.totalRetailOfCoveredItems ?? 0,
      updatedBy: saleIntegrityData?.updatedBy ?? '',
      updatedOn: formatDate(saleIntegrityData?.updatedOn),
      uploadProofOfPurchase: saleIntegrityData?.uploadProofOfPurchase ?? '',
      hasSMSNoSolicitFlg: saleIntegrityData?.hasSMSNoSolicitFlg,
      subcategory: saleIntegrityData?.subcategory ?? '',
    },
    validationSchema,
    onSubmit: (values) => {
      const matchedSub = subcategories.find(
        (s) => s.subCatCode === values?.subcategory || s?.subCatDescription === values?.subcategory,
      )
      const finalSubcategoryCode = matchedSub ? matchedSub?.subCatCode : values?.subcategory

      const payload: any = {
        ...saleIntegrityData,
        ...values,
        subcategory: finalSubcategoryCode,
        dealerSalesId: Number(values.dealerSalesId),
        quantity: Number(values.quantity),
        retailPrice: Number(values.retailPrice),
        retailProtectionPrice: Number(values.retailProtectionPrice),
        tax: Number(values.tax),
        totalInvoiceAmount: Number(values.totalInvoiceAmount),
        totalRetailOfCoveredItems: Number(values.totalRetailOfCoveredItems),
        isFrom: 'opwIntranet',
        updatedBy: `${user?.firstName} ${user?.lastName}`,
      }
      updateDealerSale(payload)
    },
  })

  useEffect(() => {
    if (uIsSuccess) {
      const msg = (uData as any)?.message || 'Sale record updated successfully'
      toast.success(msg)
      onClose()
      uReset()
    }
    if (uIsError) {
      toast.error((uError as any)?.data?.message ?? 'Update Failed')
      uReset()
    }
  }, [uIsSuccess, uIsError, onClose, uData, uError, uReset])

  const renderField = (
    name: keyof typeof formik.initialValues,
    label: string,
    disabled = false,
  ) => {
    if (name === 'subcategory') {
      const sortedSubcategories = [...subcategories].sort((a, b) => {
        const descA = String(a.subCatDescription || '')
          .trim()
          .toLowerCase()
        const descB = String(b.subCatDescription || '')
          .trim()
          .toLowerCase()

        return descA.localeCompare(descB, 'en', { sensitivity: 'base' })
      })

      return (
        <Item key={name}>
          <Autocomplete
            fullWidth
            size='small'
            id={name}
            options={sortedSubcategories}
            loading={subcategoriesLoading}
            getOptionLabel={(option) => option.subCatDescription || ''}
            isOptionEqualToValue={(option, value) => option.subCatCode === value?.subCatCode}
            value={
              sortedSubcategories.find(
                (item) =>
                  item.subCatCode === formik.values[name] ||
                  item.subCatDescription === formik.values[name],
              ) || null
            }
            onChange={(_, newValue) => {
              formik.setFieldValue(name, newValue ? newValue.subCatCode : '')
              formik.setFieldTouched(name, true)
            }}
            disabled={disabled}
            renderInput={(params) => (
              <TextField
                {...params}
                margin='dense'
                label={label}
                required
                error={formik.touched.subcategory && Boolean(formik.errors.subcategory)}
                helperText={formik.touched.subcategory && formik.errors.subcategory}
                InputProps={{
                  ...params.InputProps,
                  sx: { borderRadius: '8px' },
                  endAdornment: (
                    <>
                      {subcategoriesLoading ? <CircularProgress color='inherit' size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Item>
      )
    }

    if (name === 'dealerGroupNumber') {
      return (
        <Item key={name}>
          <TextField
            select
            fullWidth
            size='small'
            margin='dense'
            id={name}
            name={name}
            label={label}
            value={formik.values[name] ?? ''}
            onChange={(e) => {
              formik.handleChange(e)
              formik.setFieldValue('dealerId', '')
            }}
            disabled={disabled}
            InputProps={{ sx: { borderRadius: '8px' } }}
          >
            {dealerData.map((group, i) => (
              <MenuItem key={i} value={group.dealerGroupNumber}>
                {group.dealerGroupNumber}
              </MenuItem>
            ))}
          </TextField>
        </Item>
      )
    }

    if (name === 'dealerId') {
      const selectedGroup = dealerData.find(
        (g) => g.dealerGroupNumber === formik.values.dealerGroupNumber,
      )
      const dealerList = selectedGroup ? selectedGroup.dealerIdList : []

      return (
        <Item key={name}>
          <TextField
            select
            fullWidth
            size='small'
            margin='dense'
            id={name}
            name={name}
            label={label}
            value={formik.values[name] ?? ''}
            onChange={formik.handleChange}
            disabled={disabled || !formik.values.dealerGroupNumber}
            InputProps={{ sx: { borderRadius: '8px' } }}
          >
            {dealerList.length === 0 && (
              <MenuItem value=''>
                {formik.values.dealerGroupNumber ? 'No IDs found' : 'Select Group First'}
              </MenuItem>
            )}
            {dealerList.map((item: any, i: number) => (
              <MenuItem key={i} value={item.dealerId}>
                {item.dealerId}
              </MenuItem>
            ))}
          </TextField>
        </Item>
      )
    }

    if (name === 'state') {
      return (
        <Item key={name}>
          <TextField
            select
            fullWidth
            size='small'
            margin='dense'
            id={name}
            name={name}
            label={label}
            value={formik.values[name] ?? ''}
            onChange={formik.handleChange}
            disabled={disabled || statesLoading || countriesLoading || !countryId}
            InputProps={{ sx: { borderRadius: '8px' } }}
          >
            {!countryId ? (
              <MenuItem value='' disabled>
                Select a valid country first
              </MenuItem>
            ) : statesList.length === 0 ? (
              <MenuItem value='' disabled>
                {statesLoading ? 'Loading...' : 'No states available'}
              </MenuItem>
            ) : (
              statesList.map((stateItem: StateList) => (
                <MenuItem
                  key={stateItem.stateId}
                  value={stateItem.stateCode || stateItem.stateName}
                >
                  {stateItem.stateName}
                </MenuItem>
              ))
            )}
          </TextField>
        </Item>
      )
    }

    if (name === 'brand') {
      const currentValue = formik.values[name] ?? ''

      return (
        <Item key={name}>
          <TextField
            select
            fullWidth
            size='small'
            margin='dense'
            id={name}
            name={name}
            label={label}
            value={currentValue}
            onChange={formik.handleChange}
            disabled={disabled || brandsLoading}
            InputProps={{ sx: { borderRadius: '8px' } }}
          >
            {currentValue && (
              <MenuItem value={currentValue} sx={{ display: 'none' }}>
                {currentValue}
              </MenuItem>
            )}
            {brandsList.map((brandItem: any, i: number) => {
              const itemValue =
                brandItem?.brandCode ||
                brandItem?.brandName ||
                brandItem?.brand ||
                brandItem?.name ||
                (typeof brandItem === 'string' ? brandItem : i)
              const itemLabel =
                brandItem?.brandName ||
                brandItem?.brand ||
                brandItem?.name ||
                (typeof brandItem === 'string' ? brandItem : `Brand ${i}`)

              return (
                <MenuItem key={i} value={itemValue}>
                  {itemLabel}
                </MenuItem>
              )
            })}
          </TextField>
        </Item>
      )
    }

    const isDateField = name.endsWith('Dt') || name.endsWith('On')
    if (isDateField) {
      const dateValue = formik.values[name] as string
      const parsedDate = dateValue && dayjs(dateValue).isValid() ? dayjs(dateValue) : null

      return (
        <Item key={name}>
          <DatePicker
            label={label}
            disabled={disabled}
            format='MM/DD/YYYY'
            value={parsedDate}
            onChange={(val: any) =>
              formik.setFieldValue(name, val && val.isValid() ? val.format('YYYY-MM-DD') : '')
            }
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small',
                margin: 'dense',
                id: name,
                name: name,
                InputProps: { sx: { borderRadius: '8px' } },
              } as any,
            }}
          />
        </Item>
      )
    }

    return (
      <Item key={name}>
        <TextField
          fullWidth
          size='small'
          margin='dense'
          id={name}
          name={name}
          label={label}
          type='text'
          value={formik.values[name] ?? ''}
          onChange={formik.handleChange}
          disabled={disabled}
          InputProps={{ sx: { borderRadius: '8px' } }}
        />
      </Item>
    )
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={styles.container}>
        <Box sx={styles.headerStyle}>
          <Typography variant='h6' color='white'>
            Edit Sale to Contract Integrity Form
          </Typography>
        </Box>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={styles.content}>
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={0.5}>
                <Grid item xs={12}>
                  <Typography variant='overline' color='primary' sx={{ fontWeight: 'bold' }}>
                    System & Errors
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  {renderField('dealerSalesId', 'Dealer Sales ID', true)}
                </Grid>
                <Grid item xs={4}>
                  {renderField('application', 'Application', true)}
                </Grid>
                <Grid item xs={4}>
                  {renderField('contractSourceName', 'Contract Source')}
                </Grid>
                <Grid item xs={6}>
                  {renderField('contractNumber', 'Contract Number', true)}
                </Grid>
                <Grid item xs={6}>
                  {renderField('loanNumber', 'Loan Number')}
                </Grid>
                <Grid item xs={12}>
                  {renderField('contractCreateError', 'Error Message', true)}
                </Grid>
                <Grid item xs={12}>
                  {renderField('contractCreateErrorDetail', 'Error Detail', true)}
                </Grid>

                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Typography variant='overline' color='primary' sx={{ fontWeight: 'bold' }}>
                    Dealer & Invoice
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  {renderField('dealerGroupNumber', 'Dealer Group')}
                </Grid>
                <Grid item xs={6}>
                  {renderField('dealerId', 'Dealer ID')}
                </Grid>
                <Grid item xs={4}>
                  {renderField('invoiceTicketNumber', 'Invoice Ticket')}
                </Grid>
                <Grid item xs={4}>
                  {renderField('invoiceDt', 'Invoice Date')}
                </Grid>
                <Grid item xs={4}>
                  {renderField('invoiceLineNumber', 'Invoice Line')}
                </Grid>
                <Grid item xs={4}>
                  {renderField('salesPersonId', 'Sales Person ID')}
                </Grid>

                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Typography variant='overline' color='primary' sx={{ fontWeight: 'bold' }}>
                    Customer Information
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  {renderField('firstName', 'First Name')}
                </Grid>
                <Grid item xs={6}>
                  {renderField('lastName', 'Last Name')}
                </Grid>
                <Grid item xs={6}>
                  {renderField('email', 'Email')}
                </Grid>
                <Grid item xs={6}>
                  {renderField('phone', 'Phone')}
                </Grid>
                <Grid item xs={12}>
                  {renderField('address1', 'Address 1')}
                </Grid>
                <Grid item xs={12}>
                  {renderField('address2', 'Address 2')}
                </Grid>
                <Grid item xs={4}>
                  {renderField('city', 'City')}
                </Grid>
                <Grid item xs={4}>
                  {renderField('state', 'State')}
                </Grid>
                <Grid item xs={4}>
                  {renderField('postalCode', 'Zip Code')}
                </Grid>
                <Grid item xs={8}>
                  {renderField('country', 'Country', true)}
                </Grid>
                <Grid item xs={4}>
                  {renderField('squareFootage', 'Sq Footage')}
                </Grid>

                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Typography variant='overline' color='primary' sx={{ fontWeight: 'bold' }}>
                    Product & Pricing
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  {renderField('subcategory', 'SubCategory')}
                </Grid>
                <Grid item xs={6}>
                  {renderField('itemDescription', 'Description')}
                </Grid>
                <Grid item xs={6}>
                  {renderField('sku', 'SKU')}
                </Grid>
                <Grid item xs={3}>
                  {renderField('brand', 'Brand')}
                </Grid>
                <Grid item xs={3}>
                  {renderField('model', 'Model')}
                </Grid>
                <Grid item xs={3}>
                  {renderField('serial', 'Serial')}
                </Grid>
                <Grid item xs={3}>
                  {renderField('materialType', 'Material Type')}
                </Grid>
                <Grid item xs={3}>
                  {renderField('quantity', 'Qty')}
                </Grid>
                <Grid item xs={3}>
                  {renderField('retailPrice', 'Retail Price')}
                </Grid>
                <Grid item xs={3}>
                  {renderField('tax', 'Tax')}
                </Grid>
                <Grid item xs={3}>
                  {renderField('totalInvoiceAmount', 'Total Invoice')}
                </Grid>
                <Grid item xs={6}>
                  {renderField('totalRetailOfCoveredItems', 'Total Retail Of Covered Items')}
                </Grid>

                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Typography variant='overline' color='primary' sx={{ fontWeight: 'bold' }}>
                    Plan & Dates
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  {renderField('protectionPlanType', 'Plan Type')}
                </Grid>
                <Grid item xs={6}>
                  {renderField('retailProtectionPrice', 'Plan Price')}
                </Grid>
                <Grid item xs={3}>
                  {renderField('deliveryDt', 'Delivery Date')}
                </Grid>
                <Grid item xs={3}>
                  {renderField('installDt', 'Install Date')}
                </Grid>
                <Grid item xs={3}>
                  {renderField('planPurchaseDt', 'Purchase Date')}
                </Grid>
                <Grid item xs={3}>
                  {renderField('homeClosingDt', 'Home Closing Date')}
                </Grid>

                {/* Realtor Info section */}
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Typography variant='overline' color='primary' sx={{ fontWeight: 'bold' }}>
                    Realtor Information
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  {renderField('realtorProgram', 'Realtor Program')}
                </Grid>
                <Grid item xs={4}>
                  {renderField('realtorFirstName', 'Realtor First Name')}
                </Grid>
                <Grid item xs={4}>
                  {renderField('realtorLastName', 'Realtor Last Name')}
                </Grid>
                <Grid item xs={6}>
                  {renderField('realtorEmail', 'Realtor Email')}
                </Grid>

                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Typography variant='overline' color='primary' sx={{ fontWeight: 'bold' }}>
                    Audit Info
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  {renderField('createdBy', 'Created By', true)}
                </Grid>
                <Grid item xs={6}>
                  {renderField('createdOn', 'Created On', true)}
                </Grid>
                <Grid item xs={6}>
                  {renderField('updatedBy', 'Updated By', true)}
                </Grid>
                <Grid item xs={6}>
                  {renderField('updatedOn', 'Updated On', true)}
                </Grid>
              </Grid>
            </form>
          </Box>
        </LocalizationProvider>

        <Box sx={styles.footerStyle}>
          <Button
            onClick={onClose}
            variant='outlined'
            color='error'
            sx={{ mr: 1.5, borderRadius: '20px', textDecoration: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => formik.handleSubmit()}
            variant='contained'
            disabled={uIsLoading || !formik.dirty}
            sx={{ borderRadius: '20px', px: 4, textDecoration: 'none' }}
          >
            {uIsLoading ? <CircularProgress size={24} /> : 'Update'}
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}
